import type { FeatureSection } from './types'

export const filesSection: FeatureSection = {
  key: 'files',
  title: 'Files',
  navPage: 'files',
  routes: ['files'],
  audience: 'everyone',
  image: '/help/screenshots/files/_section.png',
  imageAlt: 'The Files page of the job: Upload, New Folder, Download, Move and Delete, the Home breadcrumb, search and view buttons, and an empty file list.',
  about:
    'The Files area holds the documents of one job: reports, exports, data files and anything else people keep with the study. Everyone signed in to the customer can browse, upload, download, move, rename and delete here. The agent reads from and saves to the same area, and one folder in it, Brand assets, sets the customer’s look across Platinum.',
  features: [
    {
      key: 'job-files',
      name: 'Files',
      what: 'The file area of the job chosen in the top bar. Each job has its own files.',
      how: 'Choose the job in the top bar, then open Files. Without a customer and job chosen, the page asks you to select them first. Files helper opens the agent in a side pane to find and download files with you.',
      controls: ['Files helper'],
      agentNotes:
        'The Files helper button opens the agent in a side pane with this page’s tools; it follows agent access (disabled with the reason when you lack it) and starts a fresh chat each time. The Files area is per job, not per customer. It is not the customer’s secure SFTP delivery area: files delivered by SFTP do not appear here.',
      image: '/help/screenshots/files/job-files.png',
      imageAlt: 'The Files page header with its actions, above the Home breadcrumb and the search box.',
    },
    {
      key: 'folders',
      name: 'Folders and breadcrumbs',
      what: 'Files sit in folders, and folders can hold folders. The breadcrumb trail above the list shows where you are, starting at Home.',
      how: 'Click a folder to open it. Click a name in the breadcrumb trail to go back up to it.',
      tools: ['open_folder', 'go_up', 'go_to_root', 'read_current_folder'],
      image: '/help/screenshots/files/folders.png',
      imageAlt: 'Inside a folder: the breadcrumb Home › Briefs above the list of the folder’s files.',
    },
    {
      key: 'search',
      name: 'Search files...',
      what: 'Finds files and folders by name across the whole job, not only the open folder.',
      how: 'Type part of a name in the search box. Results show the full path; click a folder in the results to open the folder that holds it.',
      controls: ['Search files...'],
      tools: ['search_files'],
      image: '/help/screenshots/files/search.png',
      imageAlt: 'The Search files box with a search typed in, beside the Grid View and List View buttons.',
    },
    {
      key: 'view-and-sort',
      name: 'List View and Grid View',
      what: 'Shows the files as a table with name, modification date and size, or as a grid of cards.',
      how: 'Click List View or Grid View beside the search box. In List View, click Name or Modified to sort; click again to reverse the order. Folders always come first.',
      controls: ['List View', 'Grid View', 'Name', 'Modified', 'Size'],
      tools: ['set_files_view'],
      image: '/help/screenshots/files/view-and-sort.png',
      imageAlt: 'The Grid View and List View buttons and the list’s Name, Modified and Size column headings.',
    },
    {
      key: 'select',
      name: 'Select files',
      what: 'Ticks files and folders so Download, Move and Delete in the header act on all of them at once. Ticked folders count in full: everything inside them is downloaded, moved or deleted with them.',
      how: 'Tick the box beside each item. In List View the box in the header row selects everything in the folder.',
      controls: ['Select all files'],
      tools: ['select_file', 'clear_selection'],
      image: '/help/screenshots/files/select.png',
      imageAlt: 'The Files list with two files ticked; the header now offers Download (2), Move and Delete.',
    },
    {
      key: 'upload',
      name: 'Upload',
      what: 'Adds files to the open folder.',
      how: 'Click Upload in the header and choose one or more files, or drag files onto the list. A message confirms each upload. An empty folder also shows Upload Files.',
      controls: ['Upload', 'Upload Files'],
      agentNotes:
        'A file uploaded here is in the job’s Files area. The agent reads it with `pt files list` and `pt files download <path>`; it does not see it until it looks.',
      image: '/help/screenshots/files/upload.png',
      imageAlt: 'The Files actions with the pointer on Upload.',
    },
    {
      key: 'new-folder',
      name: 'New Folder',
      what: 'Creates a folder inside the open folder.',
      how: 'Click New Folder, type the Folder Name and click Create.',
      controls: ['New Folder', 'Folder Name', 'Create', 'Cancel'],
      image: '/help/screenshots/files/new-folder.png',
      imageAlt: 'The Create New Folder dialog with the Folder Name box, Cancel and Create.',
    },
    {
      key: 'download',
      name: 'Download',
      what: 'Saves a file to your computer, or a folder as one zip file holding everything in it, sub-folders included.',
      how: 'Open a file’s ⋮ menu and choose Download, or a folder’s ⋮ menu and choose Download as ZIP. To download several, tick them and click Download in the header: one ticked file saves as itself, anything more saves as one zip of every ticked file and folder.',
      controls: ['Download', 'Download as ZIP'],
      tools: ['download_file', 'download_selected_files'],
      agentNotes: 'Clicking a file name does not open or download it. Downloading is through the ⋮ menu or the header Download button. One zip holds at most 5,000 files and 1 GB (before compression); a bigger download is refused with that reason, so download its folders one at a time. The zip is assembled in the browser before it saves, so a large one takes a while to appear.',
      image: '/help/screenshots/files/download.png',
      imageAlt: 'The Files actions with the pointer on Download.',
    },
    {
      key: 'rename',
      name: 'Rename',
      what: 'Changes the name of a file or folder.',
      how: 'Open the item’s ⋮ menu, choose Rename, type the new name and click Rename.',
      controls: ['Rename', 'New File Name', 'New Folder Name'],
      agentNotes: 'Renaming the Brand assets folder, or brand.json inside it, removes the customer’s brand until it is put back.',
      image: '/help/screenshots/files/rename.png',
      imageAlt: 'The Rename dialog for a file, with the New File Name box.',
    },
    {
      key: 'move',
      name: 'Move',
      what: 'Moves files and folders into another folder. A folder moves with everything in it.',
      how: 'Open an item’s ⋮ menu and choose Move, or tick several files and folders and click Move in the header. Pick the Target folder (Root is the top level) and click Move. A message says when everything moved.',
      controls: ['Move', 'Target folder', 'Root'],
      agentNotes: 'A folder cannot move into itself or one of its own folders, so those are not offered as targets. A move is refused when the target already has a folder of that name. A folder move copies everything first and removes the old copies only when every copy worked; if a copy fails, nothing moves and the message says why. Moving the Brand assets folder out of the top level removes the customer’s brand until it is moved back.',
      image: '/help/screenshots/files/move.png',
      imageAlt: 'The Move File dialog with the Target folder list open: Root, Briefs and Data.',
    },
    {
      key: 'delete',
      name: 'Delete',
      what: 'Removes files or folders. Deleting a folder removes everything in it.',
      how: 'Open the item’s ⋮ menu and choose Delete, or tick several items and click Delete in the header. Confirm with Delete in the Confirm Deletion dialog.',
      controls: ['Delete', 'Confirm Deletion', 'Cancel'],
      agentNotes: 'There is no undo or recycle bin on this page. Anyone signed in to the customer can delete here; it is not limited to Managers.',
      image: '/help/screenshots/files/delete.png',
      imageAlt: 'The Confirm Deletion dialog for a file, with Cancel and Delete.',
    },
    {
      key: 'agent-files',
      name: 'Agent Reports',
      what: 'The agent can read the job’s files and save its work here. By default it saves into the Agent Reports folder.',
      how: 'In a session, name the file and ask the agent to use it, or ask it to save a deck, workbook or app to Files. It appears in Files once saved.',
      agentNotes:
        'The agent uses `pt files list [--folder Docs]`, `pt files download <path>` (into the session’s workspace) and `pt files upload <local-path> [--folder "Docs/Agent Reports"]`. It works only on a job the session has selected; with several jobs it must pass `--job`. Listing, downloading and uploading all stay inside the Files area: paths under `Docs/`. A file attached in the composer goes to the session, not to Files.',
    },
    {
      key: 'brand-assets',
      name: 'Brand assets',
      audience: 'realm',
      what: 'One folder in Files, Brand assets, holds your company’s look: its colours, fonts, logos, PowerPoint template and start-screen cover pictures. Platinum uses it for web apps, decks, the card shown when a link is shared, the first brand theme on dashboards and the agent start screen.',
      how: 'To change a logo, template or cover picture, replace that file in the folder. To change colours or fonts, ask your Bayes Price contact. New web apps and decks pick up a change within a few minutes. Moving, renaming or deleting the folder removes your look until it is put back.',
      controls: ['Brand assets'],
      agentNotes:
        'There is one brand per customer, found by looking for `Brand assets/brand.json` across the customer’s jobs. Without the folder, Platinum falls back to its built-in look. A team can carry its own overrides under `teams/<team id>/` inside the folder; anything a team does not set comes from the customer’s brand. The agent reads the brand with `pt brand`, writes the web app skin with `pt brand pull`, and gets deck settings with `pt brand deck-config`. Do not copy brand colours anywhere else. Anyone who can upload to Files can change this folder.',
    },
  ],
  steps: [
    'Choose the job in the top bar and open Files.',
    'Open a folder, or search for a file by name.',
    'Upload files by dragging them in or with Upload.',
    'Tick files to download, move or delete several at once, or use an item’s ⋮ menu.',
    'Ask the agent in a session to read a file from Files or save its work there.',
  ],
  // Shown as pills in this section's page helpers and the Help pane until the first message (Admin → System context replaces them per customer).
  starters: [
    'What is in this folder?',
    'Help me find a file',
    'How do I download several files at once?',
  ],
  intro: 'Which file or folder can I help you with?',
}
